import type { MenuProps, PositioningProps } from '@fluentui/react-components'
import { useCallback, useEffect, useRef, useState } from 'react'

const OPEN_DELAY_MS = 200
// Long enough to cross the gap between the tag and the popover without closing.
const CLOSE_DELAY_MS = 150
// Left edge of the chat input (Home's chatInputContainer padding), measured from the task pane edge.
const PROMPT_WINDOW_LEFT_PX = 8

const POPOVER_POSITIONING: PositioningProps = {
  position: 'above',
  align: 'start',
  autoSize: 'height',
  offset: ({ targetRect }) => ({ mainAxis: 4, crossAxis: PROMPT_WINDOW_LEFT_PX - targetRect.x }),
}

export function useSourcesPopover() {
  const [isOpen, setIsOpen] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const openedByHoverRef = useRef(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dismissedByMouseLeaveRef = useRef(false)

  useEffect(() => {
    if (isOpen || !dismissedByMouseLeaveRef.current) return
    dismissedByMouseLeaveRef.current = false
    const trigger = triggerRef.current
    if (trigger?.ownerDocument.activeElement === trigger) trigger?.blur()
  }, [isOpen])

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  useEffect(() => clearTimer, [clearTimer])

  const scheduleOpen = useCallback(
    (delay: number) => {
      clearTimer()
      if (isOpen) return
      timerRef.current = setTimeout(() => {
        openedByHoverRef.current = true
        setIsOpen(true)
      }, delay)
    },
    [clearTimer, isOpen]
  )

  // Fluent only closes a hover menu when the trigger is left, so leaving the popover must close it too.
  const scheduleClose = useCallback(() => {
    clearTimer()
    timerRef.current = setTimeout(() => {
      openedByHoverRef.current = false
      dismissedByMouseLeaveRef.current = true
      setIsOpen(false)
    }, CLOSE_DELAY_MS)
  }, [clearTimer])

  const openNow = useCallback(() => {
    clearTimer()
    setIsOpen(true)
  }, [clearTimer])

  const handleOpenChange: NonNullable<MenuProps['onOpenChange']> = useCallback(
    (_event, data) => {
      clearTimer()
      dismissedByMouseLeaveRef.current = false
      const openedByHover = openedByHoverRef.current
      openedByHoverRef.current = false
      if (
        !data.open &&
        data.type === 'menuTriggerClick' &&
        openedByHover &&
        !data.keyboard &&
        data.event.detail > 0
      )
        return
      setIsOpen(data.open)
    },
    [clearTimer]
  )

  return {
    menuProps: { open: isOpen, onOpenChange: handleOpenChange, positioning: POPOVER_POSITIONING },
    triggerProps: {
      ref: triggerRef,
      onMouseEnter: () => scheduleOpen(OPEN_DELAY_MS),
      onMouseLeave: scheduleClose,
    },
    popoverProps: { onMouseEnter: openNow, onMouseLeave: scheduleClose },
  }
}
